/**
 * Cache Utils
 * this file contains any cache related utils used for setting, getting or clearing cache
 *
 * Naming Convention: set${Cache_Item}Cache, get${Cache_Item}Cache, clear${Cache_Item}Cache
 * i.e. setExampleCache, getExampleCache, clearExampleCache
 *
 */

// service
import { $localStorage } from "@tajawal/web-core-sdk-v2/lib/services/local-storage.service";
import { $sessionStorage } from "@tajawal/web-core-sdk-v2/lib/services/session-storage.service";
import { $cookie } from "@tajawal/web-core-sdk-v2/lib/services/cookie.service";

// utils
import { isValidJwt } from "@/utils/compute.utils";
import {
  getParsedSearchQuery,
  getParamsFromQuery,
} from "@tajawal/web-flights-sdk/lib/utils/search.utils";
import _pick from "lodash/pick";
import _keyBy from "lodash/keyBy";
import _get from "lodash/get";
import _isArray from "lodash/isArray";
import _map from "lodash/map";
import _remove from "lodash/remove";
import _isPlainObject from "lodash/isPlainObject";
import _includes from "lodash/includes";
import _split from "lodash/split";
import _slice from "lodash/slice";
import _filter from "lodash/filter";
import _isEmpty from "lodash/isEmpty";
import { parse, differenceInMinutes } from "date-fns";
import { v4 as uuidv4 } from "uuid";

// constants
import {
  COOKIES,
  COOKIE_KEYS,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
} from "@/constants/cache.constants";
import {
  CONTACT_MEDIUM_FIELDS,
  RECENT_SEARCH_RESULTS_LIMIT,
} from "@/constants/common.constants";
import { DATE_FORMAT_MAP } from "@/constants/date.constants";
import {
  TRAVELLER_CACHE_VALUES,
  SEARCH_BOX_CONSTANTS,
} from "@tajawal/web-flights-sdk/lib/constants";

import {
  CachedTraveller,
  TravellerModel,
} from "@tajawal/web-flights-sdk/lib/types/traveller.types";
import { ContactModel } from "@tajawal/web-flights-sdk/lib/types/contact.types";

export function isUserLoggedIn(): boolean {
  const authToken = $cookie.get(COOKIE_KEYS.AUTH_TOKEN);
  return isValidJwt(authToken);
}

export const TravellerCache = {
  get(): Record<string, CachedTraveller> {
    if ($localStorage.isFake()) {
      return {};
    }
    return $localStorage.get(LOCAL_STORAGE_KEYS.CACHED_TRAVELLERS_MAP) || {};
  },
  set(travellers: TravellerModel[]): void {
    if (!travellers || !travellers.length || $localStorage.isFake()) return;
    const travellerList = travellers.map((traveller) => ({
      ..._pick(traveller, [
        ...TRAVELLER_CACHE_VALUES,
        "isValueSavedOnce",
        "isComplete",
        "isDirty",
      ]),
      isCachedTraveller: true,
    }));
    $localStorage.set(
      LOCAL_STORAGE_KEYS.CACHED_TRAVELLERS_MAP,
      _keyBy(travellerList, "id"),
    );
  },
};

export const ContactCache = {
  set(contact: ContactModel): void {
    if (!_isPlainObject(contact) || $localStorage.isFake()) return;
    $localStorage.set(LOCAL_STORAGE_KEYS.CACHED_CONTACT, contact);
  },
  get(): ContactModel {
    if ($localStorage.isFake()) {
      return {} as ContactModel;
    }
    return $localStorage.get(LOCAL_STORAGE_KEYS.CACHED_CONTACT) || {};
  },
};

export const RecentSearchCache = {
  get() {
    const { FLIGHTS_RECENT_QUERIES } = SEARCH_BOX_CONSTANTS;
    let recentList = $localStorage.get(FLIGHTS_RECENT_QUERIES);
    recentList = _isArray(recentList) ? recentList : [];

    const filteredList = _filter(recentList, (item) => !_isEmpty(item));

    const queriesList = _map(filteredList, ({ query }) => {
      return { parsedQuery: getParsedSearchQuery(query), query };
    });

    const currentDate = new Date();
    _remove(
      queriesList,
      (v: any, i) =>
        differenceInMinutes(
          currentDate,
          parse(
            v.parsedQuery.departureDate,
            DATE_FORMAT_MAP[SEARCH_BOX_CONSTANTS.REQUEST_DATE_FORMAT],
            new Date(),
          ),
        ) > 1 || i > RECENT_SEARCH_RESULTS_LIMIT,
    );

    return queriesList;
  },
  add(query: string) {
    const trimmedQuery = _split(query, "?")[0];
    const { originId, destinationId } = getParamsFromQuery(query);
    const hasOriginAndDestination = originId && destinationId;
    const { FLIGHTS_RECENT_QUERIES, MAX_SAVED_QUERY_NUMBER } =
      SEARCH_BOX_CONSTANTS;
    const queriesArray = $localStorage.get(FLIGHTS_RECENT_QUERIES);
    const savedQueriesArray = _slice(queriesArray, 0, MAX_SAVED_QUERY_NUMBER);

    if (
      _includes(savedQueriesArray, trimmedQuery) ||
      !hasOriginAndDestination
    ) {
      return false;
    }

    if (savedQueriesArray.length >= MAX_SAVED_QUERY_NUMBER) {
      savedQueriesArray.pop();
    }

    savedQueriesArray.unshift(trimmedQuery);
    $localStorage.set(FLIGHTS_RECENT_QUERIES, savedQueriesArray);
    return true;
  },
  clear() {
    const { FLIGHTS_RECENT_QUERIES } = SEARCH_BOX_CONSTANTS;
    $localStorage.set(FLIGHTS_RECENT_QUERIES, []);
  },
};

export function getUniqueId(): string {
  const storedUuId = $localStorage.get(LOCAL_STORAGE_KEYS.BROWSER_SESSION_ID);
  if (storedUuId) {
    return storedUuId;
  }
  const newUuId = uuidv4();
  $localStorage.set(LOCAL_STORAGE_KEYS.BROWSER_SESSION_ID, newUuId);
  return newUuId;
}

export function getPartnerReferer(query) {
  const partnerFromStorage = $sessionStorage.get(
    SESSION_STORAGE_KEYS.PARTNER_REFERER,
  );
  const partnerFromRouter = query?.partner_referer ?? "";

  if (partnerFromRouter) {
    $sessionStorage.set(
      SESSION_STORAGE_KEYS.PARTNER_REFERER,
      partnerFromRouter,
    );
    $cookie.set(SESSION_STORAGE_KEYS.PARTNER_REFERER, partnerFromRouter);
  }

  return partnerFromRouter || partnerFromStorage;
}

export const GlobalPOSCookie = {
  add(value: string) {
    const expiryDate = new Date(new Date().getTime() + 60 * 60 * 1000);
    $cookie.set(COOKIES.USER_SELECTED_POS, value, {
      path: "/",
      expires: expiryDate,
      domain: process.env.NODE_ENV === "development" ? "" : "almosafer.com",
    });
  },
  get() {
    return $cookie.get(COOKIES.USER_SELECTED_POS, { path: "/" });
  },
};

export const BookingForOthersSession = {
  set(value) {
    $sessionStorage.set(
      SESSION_STORAGE_KEYS.BOOOK_FOR_OTHERS_TOGGLE_STATE_KEY,
      value,
    );
  },
  get(): boolean {
    return (
      !!$sessionStorage.get(
        SESSION_STORAGE_KEYS.BOOOK_FOR_OTHERS_TOGGLE_STATE_KEY,
      ) || false
    );
  },
};

export const ContactMediumCache = {
  get() {
    return $sessionStorage.get(SESSION_STORAGE_KEYS.CACHED_CONTACT_MEDIUM);
  },
  set(contactMedium) {
    if (!_isPlainObject(contactMedium)) return;
    $sessionStorage.set(
      SESSION_STORAGE_KEYS.CACHED_CONTACT_MEDIUM,
      contactMedium,
    );
  },
};

export const setContactCacheFromForm = (
  formValues,
  isBookingForOther = true,
) => {
  if (isBookingForOther) {
    ContactCache.set(_get(formValues, "contact", {}));
  } else {
    const contactState = _get(formValues, "contact", {});
    ContactMediumCache.set(_pick(contactState, CONTACT_MEDIUM_FIELDS));
  }
};

export function getBookingForOthersSessionState() {
  return (
    $sessionStorage.get(
      SESSION_STORAGE_KEYS.BOOOK_FOR_OTHERS_TOGGLE_STATE_KEY,
    ) || false
  );
}
